import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { GenericEntitySelectButton } from '../RTLSearchSelectCtrl';
import { AccentComboUnbound } from '../unbound/AccentComboUnbound';
import { t } from '../../services/HelperService';
import { listWorkflowCardWorkflows, previewWorkflowCard } from './workflowApi';
import { WorkflowCardV2Renderer } from './tracking/WorkflowCardV2Renderer';
import { workflowCardFrameStyle } from './tracking/WorkflowKanbanCard';
import {
  buildWorkflowCardContext,
  buildWorkflowCardDataShapeJson,
  buildWorkflowCardLayoutJson,
  buildWorkflowCardQueries,
  stripSerializerMetadata,
} from './tracking/workflowCardRuntime';

const safeString = (v) => (v ?? '').toString();

/** Mirrors WorkflowCardSessionProfile.UnknownStageKey (C#): the "any other stage" evaluation. */
export const WORKFLOW_CARD_UNKNOWN_STAGE_KEY = '__unknownStage';

/**
 * Outer width of a board card (a 280px column minus its 2px borders and 10px body
 * padding - see WorkflowKanbanColumn); the preview renders each stage at the real size.
 */
const CARD_WIDTH = 256;

// ---- localStorage: last viewed workflow (per card) and test entity (per entity type) ----
// The entity key is shared with the card designer so a test record chosen in one place
// is the default in the other.
const entityStorageKey = (entityType) => (safeString(entityType).trim() ? `workflowCardPreviewEntity.${safeString(entityType).trim()}` : '');
const workflowStorageKey = (entityType) => (safeString(entityType).trim() ? `workflowCardPreviewWorkflow.${safeString(entityType).trim()}` : '');

const readStored = (key) => {
  if (!key) return null;
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const writeStored = (key, value) => {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value ?? null));
  } catch {
    // Ignore localStorage failures.
  }
};

/**
 * Normalises a workflow row (host context JSON or the Workflows query) to
 * { id, name, explicit, usesCard, stages: [{ key, name }] }. usesCard: the workflow
 * renders this card (explicitly or as the entity type's default); same-type workflows
 * that do not are still listed so a card can be designed before it is assigned.
 */
const normalizeWorkflow = (w) => {
  const explicit = !!(w?.explicit ?? w?.Explicit);
  return {
    id: Number(w?.id ?? w?.ID ?? w?.workflowDefinitionID ?? w?.WorkflowDefinitionID ?? 0),
    name: safeString(w?.name ?? w?.Name),
    explicit,
    usesCard: explicit || !!(w?.usesCard ?? w?.UsesCard),
    stages: (Array.isArray(w?.stages ?? w?.Stages) ? (w?.stages ?? w?.Stages) : [])
      .map((s) => ({ key: safeString(s?.key ?? s?.stageKey ?? s?.StageKey), name: safeString(s?.name ?? s?.stageName ?? s?.StageName) }))
      .filter((s) => s.key),
  };
};

const workflowOptionLabel = (w) => {
  if (w.explicit) return w.name;
  if (w.usesCard) return `${w.name} (${t('default card')})`;
  return `${w.name} (${t('not using this card')})`;
};

/** Builds the host context the designer sends the session (and this preview reads back). */
export const buildWorkflowCardHostContext = ({ entityType, cardName, cardDefinitionID, isDefault, workflows }) => ({
  entityType: safeString(entityType),
  cardName: safeString(cardName),
  cardDefinitionID: Number(cardDefinitionID ?? 0),
  isDefault: !!isDefault,
  workflows: (workflows ?? []).map(normalizeWorkflow).map((w) => ({
    id: w.id,
    name: w.name,
    explicit: w.explicit,
    usesCard: w.usesCard,
    stages: w.stages.map((s) => ({ key: s.key, name: s.name })),
  })),
});

/** What the prompt menu inserts for a stage: the quoted name the AI sees in the host context. */
const stagePromptSnippet = (stageName) => `"${stageName}" ${t('stage')}`;

/**
 * Right-click menu group for the assistant's PROMPT box (AccentTemplateInputUnbound
 * catalog shape): one submenu per workflow listing its stages plus "Other stages", so
 * users insert the exact stage names instead of retyping them. Null when there are no
 * workflows (the prompt box then keeps the browser's own context menu).
 */
export const buildWorkflowCardStagesPromptGroup = (workflows) => {
  const list = (workflows ?? []).map(normalizeWorkflow).filter((w) => w.name);
  if (list.length === 0) return null;
  const stageItems = (w) => [
    ...w.stages.map((s) => {
      const label = s.name || s.key;
      return { token: `${w.id}:${s.key}`, label, snippet: stagePromptSnippet(label) };
    }),
    { token: `${w.id}:${WORKFLOW_CARD_UNKNOWN_STAGE_KEY}`, label: t('Other stages'), snippet: t('any other stage') },
  ];
  // A single workflow needs no per-workflow submenu.
  const items = list.length === 1
    ? stageItems(list[0])
    : list.map((w) => ({ label: w.name, items: stageItems(w) }));
  return { group: t('Stages'), items };
};

/** The remembered workflow when still listed; otherwise the first workflow USING the card, else the first workflow. */
const resolveWorkflowId = (workflows, storedId) => {
  if (!workflows?.length) return 0;
  const stored = Number(storedId ?? 0);
  if (workflows.some((w) => w.id === stored)) return stored;
  return (workflows.find((w) => w.usesCard) ?? workflows[0]).id;
};

const stripStyle = { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start', padding: 10 };
// The board's own card chrome (white, rounded, shadow, 12px 14px padding, blue left
// bar): the preview must frame the fragment exactly as the kanban board will, or a
// design tuned here looks different on the board.
const cardFrameStyle = {
  ...workflowCardFrameStyle,
  width: CARD_WIDTH,
  minHeight: 64,
  boxSizing: 'border-box',
  fontSize: 13,
  overflowWrap: 'anywhere',
};
const stageLabelStyle = { fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 };
const noteStyle = { fontSize: 12, color: '#6c757d' };
const stageChipStyle = { fontSize: 11, margin: 0 };

/**
 * The workflowCard profile's preview: renders the card for ONE test entity in EVERY
 * stage of a chosen workflow (plus an "Other stages" card = the script's default branch),
 * so stage-switching designs are seen whole.
 *
 * - Workflows come from the session's host context (the designer loads them with
 *   WorkflowCardDefinition_Workflows); when absent they are fetched here. An
 *   AccentSpinner shows until they are known.
 * - The last viewed workflow (per entity type) and test entity (per entity type) are
 *   remembered in localStorage; with nothing remembered the FIRST workflow is selected.
 * - Data comes from WorkflowCard_Preview with the session's CURRENT queries as a v2
 *   data shape (the same host-composed EntityID restriction the board uses); the
 *   render script is compiled in the browser exactly as the board does it.
 */
export const WorkflowCardPreview = ({ queries, outputs, busy, hostContext }) => {
  const entityType = safeString(hostContext?.entityType).trim();
  const cardDefinitionID = Number(hostContext?.cardDefinitionID ?? 0);
  const isDefault = !!hostContext?.isDefault;
  const hostWorkflows = React.useMemo(
    () => (Array.isArray(hostContext?.workflows) ? hostContext.workflows.map(normalizeWorkflow).filter((w) => w.name) : null),
    [hostContext?.workflows]
  );

  const [workflows, setWorkflows] = React.useState(hostWorkflows);
  const [workflowsError, setWorkflowsError] = React.useState('');
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState(() => resolveWorkflowId(hostWorkflows, readStored(workflowStorageKey(entityType))));
  const [entity, setEntity] = React.useState(() => readStored(entityStorageKey(entityType)));
  const [previewData, setPreviewData] = React.useState({ queries: {}, errors: {} });
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState('');
  const [refreshTick, setRefreshTick] = React.useState(0);

  // Workflows: host context first; otherwise one fetch (spinner meanwhile).
  React.useEffect(() => {
    if (hostWorkflows) {
      setWorkflows(hostWorkflows);
      setSelectedWorkflowId((prev) => resolveWorkflowId(hostWorkflows, prev || readStored(workflowStorageKey(entityType))));
      return undefined;
    }
    if (!entityType && !cardDefinitionID) {
      setWorkflows([]);
      return undefined;
    }
    let cancelled = false;
    setWorkflows(null);
    setWorkflowsError('');
    (async () => {
      try {
        const rows = await listWorkflowCardWorkflows({ id: cardDefinitionID, entityType, isDefault });
        if (cancelled) return;
        const list = (Array.isArray(rows) ? rows : []).map(normalizeWorkflow).filter((w) => w.name);
        setWorkflows(list);
        setSelectedWorkflowId((prev) => resolveWorkflowId(list, prev || readStored(workflowStorageKey(entityType))));
      } catch (err) {
        if (cancelled) return;
        setWorkflows([]);
        setWorkflowsError(safeString(err?.message) || t('Unable to load the workflows using this card.'));
      }
    })();
    return () => { cancelled = true; };
  }, [hostWorkflows, entityType, cardDefinitionID, isDefault]);

  const selectedWorkflow = React.useMemo(
    () => (workflows ?? []).find((w) => w.id === selectedWorkflowId) ?? (workflows ?? [])[0] ?? null,
    [workflows, selectedWorkflowId]
  );

  const onWorkflowChange = (value) => {
    const id = Number(value ?? 0);
    if (!id) return;
    setSelectedWorkflowId(id);
    writeStored(workflowStorageKey(entityType), id);
  };

  const onEntitySelected = (selection) => {
    const row = Array.isArray(selection) ? selection[0] : selection;
    if (!row) return;
    const next = { ID: row.ID ?? row.id, Prompt: safeString(row.Prompt ?? row.prompt) };
    setEntity(next);
    writeStored(entityStorageKey(entityType), next);
  };

  // The session's current artifacts: queries → v2 data shape; the 'render' output → script.
  const queryNames = React.useMemo(() => (queries ?? []).map((q) => safeString(q?.name).trim()).filter(Boolean), [queries]);
  const dataShapeJson = React.useMemo(
    () => buildWorkflowCardDataShapeJson((queries ?? []).map((q) => ({ name: q?.name, sql: q?.query }))),
    [queries]
  );
  const renderScript = safeString((outputs ?? []).find((o) => safeString(o?.name).trim().toLowerCase() === 'render')?.script
    ?? (outputs ?? [])[0]?.script);

  // Card data for the test entity, debounced over query edits; re-run on Refresh.
  const entityID = Number(entity?.ID ?? entity?.id ?? 0);
  React.useEffect(() => {
    if (!entityType || !entityID) {
      setPreviewData({ queries: {}, errors: {} });
      setPreviewError('');
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const result = await previewWorkflowCard({
          entityType,
          entityID,
          dataShapeJson,
          layoutJson: buildWorkflowCardLayoutJson(renderScript),
        });
        if (cancelled) return;
        if (!result?.Success) {
          setPreviewData({ queries: {}, errors: {} });
          setPreviewError(safeString(result?.Error) || t('Unable to resolve preview data.'));
          return;
        }
        setPreviewData({
          queries: result?.Queries ?? result?.queries ?? {},
          // The web app's serializer adds "$id"/"$type" members to dictionaries; they
          // are not query errors.
          errors: stripSerializerMetadata(result?.QueryErrors ?? result?.queryErrors),
        });
      } catch (err) {
        if (!cancelled) {
          setPreviewData({ queries: {}, errors: {} });
          setPreviewError(safeString(err?.message) || t('Unable to resolve preview data.'));
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
    // renderScript is deliberately NOT a dependency: the script renders client-side,
    // only the queries need a server round trip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityID, dataShapeJson, refreshTick]);

  const ctxQueries = React.useMemo(() => buildWorkflowCardQueries(previewData.queries, queryNames), [previewData.queries, queryNames]);
  const entityLabel = entity?.Prompt ? `${entity.Prompt} (#${entityID})` : '';
  const queryErrorEntries = Object.entries(previewData.errors ?? {}).filter(([, m]) => !!m);

  const buildCardCtx = (stageKey, stageName) => buildWorkflowCardContext({
    entityID: entityID || '',
    entityType,
    stageKey,
    stageName,
    workflowName: selectedWorkflow?.name ?? '',
    title: entity?.Prompt ?? '',
    description: entity?.Prompt ?? '',
  });

  const renderStageCard = (stageKey, stageName, label) => (
    <div key={stageKey || label}>
      <div style={stageLabelStyle}>{label}</div>
      <div style={cardFrameStyle}>
        <WorkflowCardV2Renderer
          render={renderScript}
          queries={ctxQueries}
          card={buildCardCtx(stageKey, stageName)}
          fallback={<div style={noteStyle}>{t('The render script produced no HTML for this stage.')}</div>}
        />
      </div>
    </div>
  );

  if (!workflows) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 160 }}>
        <AccentSpinner />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260 }}>
          <AccentComboUnbound
            key={`${entityType}:${workflows.length}:${selectedWorkflow?.id ?? 0}`}
            data={workflows.map((w) => ({ ID: w.id, Name: workflowOptionLabel(w) }))}
            valueField="ID"
            displayField="Name"
            defaultValue={selectedWorkflow?.id ?? null}
            disabled={busy || workflows.length === 0}
            onChange={onWorkflowChange}
            openOnFocus
          />
        </div>
        <GenericEntitySelectButton
          entity={entityType}
          singleSelection
          disabled={!entityType || busy}
          onSelected={onEntitySelected}
        >
          {t('Select test entity')}
        </GenericEntitySelectButton>
        <span style={noteStyle}>{entityLabel || t('No test entity selected — the cards render with empty query data.')}</span>
        <AccentButton outline size="sm" disabled={busy || previewLoading || !entityID} onClick={(e) => { setRefreshTick((n) => n + 1); e?.promise?.(); }}>
          {t('Refresh data')}
        </AccentButton>
        {previewLoading && <span style={noteStyle}>{t('Loading card data...')}</span>}
      </div>

      {selectedWorkflow && (
        // Always visible (even before any design exists) so the stage names can be
        // quoted to the assistant; the key is in the tooltip. Same names the AI gets
        // in the host context.
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={noteStyle} title={t('Right-click in the prompt to insert a stage name.')}>{t('Stages')}:</span>
          {selectedWorkflow.stages.map((s) => (
            <span key={s.key} className="wf-card-chip" style={stageChipStyle} title={s.key}>{s.name || s.key}</span>
          ))}
          <span className="wf-card-chip" style={{ ...stageChipStyle, opacity: 0.6 }} title={WORKFLOW_CARD_UNKNOWN_STAGE_KEY}>{t('Other stages')}</span>
          {selectedWorkflow.stages.length === 0 && <span style={noteStyle}>{t('(this workflow has no stages)')}</span>}
        </div>
      )}

      {workflowsError && <div className="alert alert-warning py-1 mb-0">{workflowsError}</div>}
      {previewError && <div className="alert alert-warning py-1 mb-0">{previewError}</div>}
      {queryErrorEntries.map(([name, message]) => (
        <div key={name} className="alert alert-warning py-1 mb-0">{t('Query')} '{name}': {message}</div>
      ))}

      <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #dee2e6', borderRadius: 6, background: '#f4f5f7' }}>
        {workflows.length === 0 ? (
          <div style={{ padding: 16, fontSize: 13, opacity: 0.75 }}>
            {entityType
              ? t('There are no workflows for this entity type yet, so only the "Other stages" card (the script\u2019s default branch) can be previewed.')
              : t('Choose the card\u2019s entity type to preview it.')}
          </div>
        ) : (
          <div style={{ padding: '10px 10px 0', fontSize: 12, color: '#495057' }}>
            {t('Every stage of')} <strong>{selectedWorkflow?.name}</strong> {t('for the same test entity. "Other stages" is what any stage not listed here (and any other workflow using this card) will show.')}
            {selectedWorkflow && !selectedWorkflow.usesCard && (
              <span style={{ marginLeft: 6, color: '#8a6d3b' }}>
                {t('This workflow does not use this card yet — assign the card to it (or make the card the entity type\u2019s default) for the board to render it.')}
              </span>
            )}
          </div>
        )}
        {!renderScript.trim() ? (
          <div style={{ padding: 16, fontSize: 13, opacity: 0.7 }}>
            {t('No card yet — describe the card you want in the conversation (e.g. "show the customer, job reference and total, with the install date once the job is scheduled").')}
          </div>
        ) : (
          <div style={stripStyle}>
            {(selectedWorkflow?.stages ?? []).map((stage) => renderStageCard(stage.key, stage.name || stage.key, stage.name || stage.key))}
            {renderStageCard(WORKFLOW_CARD_UNKNOWN_STAGE_KEY, t('Other stages'), t('Other stages'))}
          </div>
        )}
        {busy && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.6)',
            zIndex: 2,
          }}>
            <AccentSpinner />
          </div>
        )}
      </div>
    </div>
  );
};
